import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe, StripeElements } from "@stripe/stripe-js";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import UiContext from "../../../contexts/UiContext";
import { SocketContext } from "../../../contexts/SocketContext";
import { useCart } from "../../../contexts/CartContext";
import CartService from "@services/CartService";
import HelperService from "../../../services/HelperService";
import UserService from "../../../services/UserService";
import SocketService from "../../../services/SocketService";
import CartStatusEnum from "../../../models/CartStatusEnum";
import ICart from "../../../models/ICart";
import IOnCreateCartPaymentIntentDTO from "../../../models/DTO/IOnCreateCartPaymentIntentDTO";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwButton from "../../shared/OwButton/OwButton";
import ActivityIndicator from "../../shared/ActivityIndicator/ActivityIndicator";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import { EcommerceStripeCardForm } from "../EcommerceStripeCardForm/EcommerceStripeCardForm";
import { CancelInactiveCartModal } from "./CancelInactiveCartModal";

const STATUS_POLL_INTERVAL_MS = 5000;

type Props = {};

export const EcommerceInactiveCart: React.FC<Props> = () => {
    const { cartGuid } = useParams<{ cartGuid: string }>();
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);
    const { refreshCart } = useCart();
    const navigate = useNavigate();

    const [cart, setCart] = useState<ICart | null>(null);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [stripePromise, setStripePromise] = useState<Stripe | null>(null);
    const [paymentIntent, setPaymentIntent] = useState<IOnCreateCartPaymentIntentDTO | null>(null);
    const [stripe, setStripe] = useState<Stripe | null>();
    const [elements, setElements] = useState<StripeElements | null>();
    const [isRetrying, setIsRetrying] = useState(false);
    const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [selectMode, setSelectMode] = useState(false);
    const [selectedGuids, setSelectedGuids] = useState<Set<string>>(new Set());

    const cartRef = useRef<ICart | null>(null);
    cartRef.current = cart;

    const vendorForAuth = userModel.isSupplierUser ? undefined : vendorModel;

    const fetchCart = async () => {
        if (!cartGuid) return;
        try {
            const response = await CartService.getCartByGuid(cartGuid, userModel, supplierModel, vendorForAuth);
            setCart(response.cart);
            setErrored(false);
        } catch (err) {
            console.warn("Failed to fetch inactive cart", err);
            setErrored(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCart();
        document.title = "Orderwerks >> Pending Cart";
    }, [cartGuid]);

    useEffect(() => {
        if (!stripePromise && supplierModel.Supplier_Integration?.stripe_publishable_api_key) {
            loadStripe(supplierModel.Supplier_Integration.stripe_publishable_api_key).then((_stripe) => {
                setStripePromise(_stripe);
            });
        }
    }, [supplierModel]);

    // While the cart is PENDING the backend is still processing it. Poll until
    // it terminates: PROCESSED → navigate back to orders, anything else → re-fetch
    // so the action buttons show.
    useEffect(() => {
        if (!cart || cart.status !== CartStatusEnum.PENDING || !cartGuid) {
            return;
        }
        const interval = setInterval(async () => {
            try {
                const status = await CartService.getCartStatus(cartGuid, userModel, supplierModel, vendorForAuth);
                if (status.status === CartStatusEnum.PROCESSED) {
                    clearInterval(interval);
                    navigate("/ecomm/orders");
                    return;
                }
                if (status.status !== CartStatusEnum.PENDING) {
                    clearInterval(interval);
                    fetchCart();
                }
            } catch (err) {
                console.warn("Status poll failed", err);
            }
        }, STATUS_POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [cart?.status, cartGuid]);

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const handleRetry = async () => {
        if (!cartGuid) return;
        setIsRetrying(true);
        try {
            const intent = await CartService.retryCartPayment(cartGuid, userModel, supplierModel, vendorForAuth);
            setPaymentIntent(intent);
        } catch (err) {
            console.warn("Retry payment failed", err);
            uiContext.notifyFn({
                eid: "",
                title: "pending_cart_detail_errors_retry",
                message: err instanceof Error ? err.message : "pending_cart_detail_errors_retry",
            });
        } finally {
            setIsRetrying(false);
        }
    };

    const handleSubmitPayment = async () => {
        if (!stripe || !elements || !cartRef.current) {
            return;
        }
        setIsSubmittingPayment(true);
        const apiUrl = SocketService.getApiEndpointURl();
        const result = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${apiUrl}/stripe-complete-checkout/${cartRef.current.guid}`,
            },
        });
        if (result.error) {
            setIsSubmittingPayment(false);
            uiContext.notifyFn({
                eid: "",
                title: "pending_cart_detail_errors_retry",
                message: result.error.message || "pending_cart_detail_errors_retry",
            });
        }
    };

    const executeCancel = async () => {
        if (!cartGuid) return;
        closeModal();
        setIsCancelling(true);
        uiContext.setActivityIndicator(true);
        try {
            await CartService.cancelCart(cartGuid, userModel, supplierModel, vendorForAuth);
            await refreshCart().catch(() => undefined);
            uiContext.notifyFn(null, {
                message: "pending_cart_detail_cancel_success",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });
            navigate("/ecomm/orders");
        } catch (err) {
            console.warn("Cancel cart failed", err);
            uiContext.notifyFn({
                eid: "",
                title: "pending_cart_detail_errors_cancel",
                message: err instanceof Error ? err.message : "pending_cart_detail_errors_cancel",
            });
        } finally {
            setIsCancelling(false);
            uiContext.setActivityIndicator(false);
        }
    };

    const showCancelConfirm = () => {
        const itemCount = cart?.Cart_Items?.length || 0;
        const modalComponent = <CancelInactiveCartModal itemCount={itemCount} closeModalFn={closeModal} confirmFn={executeCancel} />;
        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    const toggleSelected = (guid: string) => {
        setSelectedGuids((prev) => {
            const next = new Set(prev);
            if (next.has(guid)) {
                next.delete(guid);
            } else {
                next.add(guid);
            }
            return next;
        });
    };

    const handleCopySelected = async () => {
        if (!cartGuid || selectedGuids.size === 0) return;
        setIsCopying(true);
        uiContext.setActivityIndicator(true);
        try {
            await CartService.copyCartItems(
                { fromCartGuid: cartGuid, cartItemGuids: Array.from(selectedGuids) },
                userModel,
                supplierModel,
                vendorForAuth
            );
            await refreshCart().catch(() => undefined);
            uiContext.notifyFn(null, {
                message: "pending_cart_detail_copy_success",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });
            setSelectedGuids(new Set());
            setSelectMode(false);
        } catch (err) {
            console.warn("Copy items failed", err);
            uiContext.notifyFn({
                eid: "",
                title: "pending_cart_detail_errors_copy",
                message: err instanceof Error ? err.message : "pending_cart_detail_errors_copy",
            });
        } finally {
            setIsCopying(false);
            uiContext.setActivityIndicator(false);
        }
    };

    const exitSelectMode = () => {
        setSelectMode(false);
        setSelectedGuids(new Set());
    };

    const goToOrders = () => navigate("/ecomm/orders");

    if (loading) {
        return (
            <div className={"EcommerceInactiveCart container mt-5 text-center"}>
                <ActivityIndicator active={true} />
                <div className={"mt-2"}>
                    <OwLabel title={"loading"} />
                </div>
            </div>
        );
    }

    if (errored || !cart) {
        return (
            <div className={"EcommerceInactiveCart container mt-5 text-center"}>
                <i className={"fad fa-exclamation-triangle fa-3x text-danger mb-3 d-block"}></i>
                <div className={"mb-3"}>
                    <OwLabel title={"pending_cart_detail_load_error"} />
                </div>
                <OwButton title={"back_to_orders"} fn={goToOrders} />
            </div>
        );
    }

    const status = cart.status as CartStatusEnum;
    const isAwaiting = status === CartStatusEnum.PAYMENT_PENDING;
    const canActOnCart =
        status === CartStatusEnum.PAYMENT_ERRORED || status === CartStatusEnum.FAILED || status === CartStatusEnum.PAYMENT_PENDING;

    const headerTitleKey =
        status === CartStatusEnum.PENDING
            ? "pending_cart_detail_header_pending"
            : status === CartStatusEnum.PAYMENT_PENDING
              ? "pending_cart_detail_header_awaiting"
              : status === CartStatusEnum.FAILED
                ? "pending_cart_detail_header_failed"
                : "pending_cart_detail_header_errored";

    const retryLabelKey = isAwaiting ? "pending_cart_detail_resume" : "pending_cart_detail_retry";

    const items = cart.Cart_Items?.filter((cartItem) => !cartItem.main_kit_cart_item_id) || [];

    const totalDisplay = HelperService.formatCurrency({
        value: cart.grand_total || cart.sub_total || 0,
        currency: "USD",
        precision: supplierModel.pricing_precision || 2,
    });

    return (
        <div className={"EcommerceInactiveCart container mt-3 mb-5"}>
            <div className={"d-flex justify-content-between align-items-center mb-3"}>
                <OwButton fn={goToOrders} leftIconClass={"fas fa-arrow-left"} className={"btn-link p-0"} title={"back_to_orders"} />
            </div>

            <HeaderDiv>
                <OwTitle title={headerTitleKey} isBold={true} />
                <div className={"text-small mt-1"}>
                    {items.length} <OwLabel title={items.length === 1 ? "item" : "items"} />
                    {!hidePricing ? <> · {totalDisplay}</> : null}
                </div>
            </HeaderDiv>

            <div className={"row mt-4"}>
                <div className={"col-12 col-md-8"}>
                    <div className={"mb-2"}>
                        <OwLabel title={"pending_cart_detail_items"} isBold={true} />
                    </div>
                    {items.map((cartItem) => {
                        const isSelected = selectedGuids.has(cartItem.guid);
                        return (
                            <CartItemRowDiv
                                key={cartItem.guid}
                                $selectable={selectMode}
                                $selected={isSelected}
                                onClick={() => selectMode && toggleSelected(cartItem.guid)}>
                                {selectMode ? (
                                    <div className={"me-2"}>
                                        <i className={isSelected ? "fas fa-check-square text-warning" : "far fa-square"}></i>
                                    </div>
                                ) : null}
                                <div className={"me-2 w-5r min-h-5r"}>
                                    <OwS3ImageViewer
                                        catalogItem={cartItem.Catalog_Item}
                                        isMicroView={true}
                                        attachmentIndex={0}
                                        imageIndex={IImageSizeEnum.THUMB}
                                        includeAllImages={false}
                                        hideEmptyImage={false}
                                    />
                                </div>
                                <div className={"flex-grow-1"}>
                                    <div>{cartItem.Catalog_Item?.name || "—"}</div>
                                    <div className={"text-small"}>
                                        <OwLabel title={"qty"} /> {cartItem.qty}
                                    </div>
                                </div>
                            </CartItemRowDiv>
                        );
                    })}
                </div>

                <div className={"col-12 col-md-4"}>
                    <ActionsContainerDiv>
                        {canActOnCart && !selectMode && !paymentIntent ? (
                            <>
                                <OwButton
                                    title={retryLabelKey}
                                    className={"btn-warning w-100 mb-2"}
                                    fn={handleRetry}
                                    active={isRetrying}
                                    disableOnActive={true}
                                    activeTitle={"please_wait"}
                                />
                                <OwButton
                                    title={"pending_cart_detail_select_items"}
                                    className={"w-100 mb-2"}
                                    fn={() => setSelectMode(true)}
                                />
                                <OwButton
                                    title={"pending_cart_detail_cancel_cart"}
                                    className={"btn-danger w-100"}
                                    fn={showCancelConfirm}
                                    active={isCancelling}
                                    disableOnActive={true}
                                />
                            </>
                        ) : null}

                        {canActOnCart && selectMode ? (
                            <>
                                <OwButton
                                    title={
                                        selectedGuids.size === 0
                                            ? "pending_cart_detail_select_items_hint"
                                            : "pending_cart_detail_copy_selected"
                                    }
                                    className={"btn-warning w-100 mb-2"}
                                    fn={handleCopySelected}
                                    disabled={selectedGuids.size === 0 || isCopying}
                                    active={isCopying}
                                    disableOnActive={true}
                                    activeTitle={"please_wait"}
                                />
                                <OwButton title={"cancel"} className={"w-100"} fn={exitSelectMode} />
                            </>
                        ) : null}

                        {!canActOnCart ? <OwButton title={"back_to_orders"} className={"w-100"} fn={goToOrders} /> : null}
                    </ActionsContainerDiv>

                    {paymentIntent && stripePromise ? (
                        <PaymentFormContainerDiv>
                            <div className={"mb-2"}>
                                <OwLabel title={"2_payment_method"} isBold={true} />
                            </div>
                            <Elements stripe={stripePromise} options={paymentIntent as any}>
                                <EcommerceStripeCardForm
                                    socket={socket}
                                    cart={cart}
                                    isCartValid={true}
                                    setPaymentFormRef={() => {}}
                                    setElements={(_elements) => setElements(_elements)}
                                    setStripe={(_stripe) => setStripe(_stripe)}
                                />
                            </Elements>
                            <OwButton
                                title={retryLabelKey}
                                className={"btn-warning w-100 mt-3"}
                                fn={handleSubmitPayment}
                                active={isSubmittingPayment}
                                disableOnActive={true}
                                activeTitle={"please_wait"}
                                disabled={!stripe || !elements}
                            />
                        </PaymentFormContainerDiv>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

const HeaderDiv = styled.div`
    border-bottom: 1px solid var(--main-text-color);
    padding-bottom: 0.75rem;
`;

const CartItemRowDiv = styled.div<{ $selectable?: boolean; $selected?: boolean }>`
    display: flex;
    align-items: center;
    padding: 0.5rem;
    margin-bottom: 0.5rem;
    border: 1px solid ${({ $selected }) => ($selected ? "var(--bs-warning, rgb(248, 153, 56))" : "var(--main-text-color)")};
    border-radius: 0.5rem;
    cursor: ${({ $selectable }) => ($selectable ? "pointer" : "default")};
`;

const ActionsContainerDiv = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid var(--main-text-color);
    border-radius: 0.5rem;
    padding: 0.75rem;
`;

const PaymentFormContainerDiv = styled.div`
    margin-top: 1rem;
    border: 1px solid var(--main-text-color);
    border-radius: 0.5rem;
    padding: 0.75rem;
`;
